import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { CheckIcon } from 'client/blocks/icons/CheckIcon';
import { getPreviewFromOrigin } from 'client/utils/image';
import * as keyset from 'i18n/DraftControlPanel';
import moment from 'moment';

import { routes } from '../../lib/routes';
import { Channel, IAppCard } from '../../model/skill';
import { IconMedium } from '../AppIcons/AppIcons';
import { getMapChannelToAppCard } from '../icons/mapChannelToAppCard';

import styles from './AppCard.module.scss';
import { AppCardPicture } from './AppCardPicture';

interface Props {
    card: IAppCard;
}

const t = i18n(keyset);

export const AppCard = ({ card }: Props) => {
    const mapChannelToAppCard = useMemo(() => getMapChannelToAppCard(), []);
    const content = mapChannelToAppCard?.[card.channel];

    const getAliceAppRoute = () => {
        if (card.channel === Channel.SmartHome) {
            return routes.app.installableAppSettings(Channel.AliceSkill, true, 'main', card.id);
        }
        return routes.app.installableAppSettings(card.channel, true, 'main', card.id);
    };

    if (card.channel === Channel.AliceSkill || card.channel === Channel.SmartHome) {
        return (
            <ErrorBoundary>
                <Link to={getAliceAppRoute()}>
                    <div
                        className={clx(
                            styles.installableAppCard__container,
                            styles.installableAppCard__container_aliceSkill,
                        )}
                    >
                        <div
                            className={clx(
                                styles.installableAppCard__contentRow,
                                styles.installableAppCard__contentRow_aliceSkill,
                            )}
                        >
                            <h3 className={styles.installableAppCard__title}>{card.title}</h3>
                            <AppCardPicture channel={card.channel} logoUrl={getPreviewFromOrigin(card?.logoUrl)} />
                        </div>
                        <div
                            className={clx(
                                styles.installableAppCard__statusRow,
                                styles.installableAppCard__statusRow_aliceApp,
                            )}
                        >
                            <div className={styles.installableAppCard__channelIcon}>
                                {content.icon}
                                {content.label}
                            </div>
                            {card.onAir && (
                                <div className={styles.publishedBlock}>
                                    <CheckIcon />
                                    {card?.publishedAt && (
                                        <span className={styles.publishedBlock__text}>
                                            {t('published')} {moment(card.publishedAt).format('DD.MM.YY')}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </Link>
            </ErrorBoundary>
        );
    }

    return (
        <ErrorBoundary>
            <Link to={getAliceAppRoute()}>
                <div className={styles.installableAppCard__container}>
                    <div className={styles.installableAppCard__contentRow}>
                        <div className={styles.installableAppCard__titleContainer}>
                            <div className={styles.installableAppCard__iconWrapper}>
                                <IconMedium imageUrl={card.logoUrl} />
                            </div>
                            <h3 className={styles.installableAppCard__title}>{card.title}</h3>
                        </div>
                        <AppCardPicture
                            channel={card.channel}
                            screenshotUrl={card?.firstScreenShotUrl}
                            logoUrl={card?.logoUrl}
                        />
                    </div>
                    <div className={styles.installableAppCard__statusRow}>
                        <div className={styles.installableAppCard__channelIcon}>
                            {content.icon}
                            {content.label}
                        </div>
                        {card.onAir && (
                            <div className={styles.publishedBlock}>
                                <CheckIcon />
                                {card?.publishedAt && (
                                    <span className={styles.publishedBlock__text}>
                                        {t('published')} {moment(card.publishedAt).format('DD.MM.YY')}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </Link>
        </ErrorBoundary>
    );
};
